// The partner's user id, derived from the caller's own pair.
//
// The profile read is `GET /api/v1/profile/{member_user_id}` (profile spec
// §5.1), so the client needs an id it does not own. It gets one the same way a
// human does — by reading the pair it already belongs to — rather than by
// having the profile response hand it over, which would make the path
// parameter decorative.
//
// Resolving through the pair rather than a "partner" shortcut route is also
// what keeps the backend check honest: the id the client sends is one it
// obtained from its own pair, and the server re-derives the partner and
// refuses anything else.

import { useQuery } from "@tanstack/react-query";
import { readPair } from "@/lib/api/pair";
import { useAuth } from "@/lib/auth/AuthProvider";
import { queryKeys } from "@/lib/queries/keys";

export function usePartnerId(): string | null {
  const { user, pairId } = useAuth();

  const { data } = useQuery({
    queryKey: queryKeys.pairs.detail(pairId ?? "none"),
    queryFn: () => readPair(pairId as string),
    enabled: Boolean(pairId),
    staleTime: 60_000,
  });

  if (!user || !data) return null;
  const partner = data.members.find((m) => m.user_id !== user.id);
  return partner?.user_id ?? null;
}
